/**
 * Executive KPI scorecards for one brand.
 *
 * Each metric carries its value, the change since a stated comparison period,
 * a benchmark, and how far the sample can be trusted. The reliability badge is
 * the important one: a number from 28 respondents and a number from 2,800 look
 * identical otherwise, and only one of them should be acted on.
 *
 * Every figure and word on a card is the agent's: the value, the change and
 * its period, the benchmark, and the reliability as sent. The panel lays them
 * out and draws the value as a bar; it adds no wording of its own.
 */

import React from "react";
import PropTypes from "prop-types";

const RELIABILITY = ["reliable", "directional", "unreliable"];

/** The number in a value or benchmark: `"88.2%"` → 88.2, `"Category Avg: 61.2"` → 61.2. */
const numberIn = (v) => {
  if (typeof v === "number") return Number.isFinite(v) ? v : undefined;
  const m = /-?\d+(?:\.\d+)?/.exec(String(v ?? "").replace(/,/g, ""));
  return m ? Number(m[0]) : undefined;
};

const isPercent = (m) => m.unit === "%" || /%\s*$/.test(String(m.value ?? ""));

/**
 * A benchmark worth marking on the bar is a level ("Category Avg: 61.2"), not
 * a gap ("vs Peer: +4.0") — a gap plotted as a position would be nonsense.
 */
const benchmarkLevel = (benchmark) =>
  typeof benchmark === "string" &&
  !/^\s*vs\b/i.test(benchmark) &&
  /avg|average|baseline|norm|mean/i.test(benchmark)
    ? numberIn(benchmark)
    : undefined;

const clamp = (n) => Math.min(100, Math.max(0, n));

/**
 * The change as sent — `1.3` with `PoP` shows as `▲ +1.3 PoP`. The arrow and
 * colour follow its sign; the number itself is not rounded or re-unitised.
 */
const Delta = ({ delta, period }) => {
  const n = Number(delta);
  if (delta === undefined || delta === null || delta === "" || Number.isNaN(n)) return null;
  const direction = n > 0 ? "up" : n < 0 ? "down" : "flat";
  const arrow = { up: "▲", down: "▼", flat: "—" }[direction];
  const sign = n > 0 && !String(delta).startsWith("+") ? "+" : "";
  return (
    <span className={`lou-kpi__delta lou-kpi__delta--${direction}`}>
      {arrow} {sign}
      {delta}
      {period ? <span className="lou-kpi__deltaPeriod"> {period}</span> : null}
    </span>
  );
};

const BrandKpiGrid = ({ brand, period, metrics = [] }) => {
  if (!metrics.length) return null;

  const cards = metrics.map((m, i) => {
    const reliability = RELIABILITY.includes(m.reliability) ? m.reliability : undefined;
    const percent = isPercent(m);
    const share = percent ? numberIn(m.value) : undefined;
    const mark = share !== undefined ? benchmarkLevel(m.benchmark) : undefined;

    return (
      <article
        className={`lou-kpi__card${m.reliability === "unreliable" ? " lou-kpi__card--lowBase" : ""}`}
        key={`${m.label}-${i}`}
      >
        <span className="lou-kpi__label">{m.label}</span>
        <span className="lou-kpi__value">
          {m.value}
          {m.unit ? <span className="lou-kpi__unit">{m.unit}</span> : null}
        </span>
        <span className="lou-kpi__slot">
          <Delta delta={m.delta} period={m.deltaPeriod} />
        </span>
        <span className="lou-kpi__slot">
          {share !== undefined ? (
            <span className="lou-kpi__meter" aria-hidden="true">
              <span className="lou-kpi__meterFill" style={{ width: `${clamp(share)}%` }} />
              {mark !== undefined ? (
                <span className="lou-kpi__meterMark" style={{ left: `${clamp(mark)}%` }} />
              ) : null}
            </span>
          ) : null}
        </span>
        <span className="lou-kpi__slot lou-kpi__caption">
          {m.benchmark ? (
            <span className="lou-kpi__benchmark">{m.benchmark}</span>
          ) : null}
          {reliability ? (
            <span className={`lou-kpi__badge lou-kpi__badge--${reliability}`}>{reliability}</span>
          ) : null}
        </span>
      </article>
    );
  });

  return (
    <section className="lou-kpi" aria-label={`Key metrics for ${brand}`}>
      <header className="lou-kpi__head">
        <h4 className="lou-kpi__brand">{brand}</h4>
        {period ? <span className="lou-kpi__period">{period}</span> : null}
      </header>

      <div className="lou-kpi__panel">
        <div className="lou-kpi__grid">{cards}</div>
      </div>
    </section>
  );
};

Delta.propTypes = {
  delta: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  period: PropTypes.string,
};
BrandKpiGrid.propTypes = {
  brand: PropTypes.string,
  period: PropTypes.string,
  metrics: PropTypes.array,
};

export default BrandKpiGrid;
